// src/components/MapaSucursal.js
//
// Mapa interactivo para asignar/editar la ubicación de una sucursal.
// Usa Google Maps JavaScript API.
//
// Claves:
//  - Render del mapa: usa REACT_APP_GOOGLE_MAPS_KEY (frontend).
//    Debe estar restringida por HTTP referer a los dominios reales
//    (localhost:3000, holaveci.co, holaveci-d5319.web.app).
//  - Búsqueda de dirección (geocoding): va por el backend
//    (/api/pos/configuracion/geocodificar). La clave del backend
//    queda solo ahí — misma arquitectura que HolaVeci mobile.
//
// Funcionalidades:
//  - Pin arrastrable. Al soltar, avisa las nuevas coordenadas.
//  - Click en el mapa mueve el pin.
//  - Buscador de dirección con Enter → llama al backend proxy → centra el pin.
//  - Botón "Usar mi ubicación" con navigator.geolocation.
//  - Si no hay coordenadas iniciales, arranca centrado en Colombia.

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleMap, MarkerF, useLoadScript } from '@react-google-maps/api';
import {
  Box, TextField, Button, Stack, InputAdornment, IconButton, Alert,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { ubicacion } from '../api/posApi';

const CENTRO_COLOMBIA = { lat: 4.5709, lng: -74.2973 };
const ZOOM_INICIAL    = 5;
const ZOOM_CON_PIN    = 16;
const MAPS_KEY        = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

const CONTAINER_STYLE = { width: '100%', height: '380px' };

// Controles del mapa: dejamos los útiles activos y solo desactivamos los
// que roban espacio o no aportan al caso de "ubicar mi sucursal".
const MAP_OPTIONS = {
  // Selector de tipo de mapa (mapa/satélite/híbrido/terreno)
  mapTypeControl: true,
  mapTypeControlOptions: {
    position: 3, // TOP_RIGHT
    style:    2, // DROPDOWN_MENU (compacto)
  },
  // Fullscreen para ver el mapa grande
  fullscreenControl: true,
  // Street View para verificar la ubicación con imagen real
  streetViewControl: true,
  streetViewControlOptions: { position: 9 }, // RIGHT_BOTTOM
  // Rotación disponible en satélite/híbrido
  rotateControl: true,
  zoomControl: true,
  // Evitar clic accidental en POIs de Google
  clickableIcons: false,
  // El scroll del mouse hace zoom sin necesitar Ctrl
  gestureHandling: 'greedy',
};

export default function MapaSucursal({
  lat, lng, onCoordsChange, notify,
  municipioNombre, departamentoNombre,
  // false en pantallas públicas (registro): la búsqueda de dirección usa un
  // endpoint autenticado del backend.
  permitirBusqueda = true,
  // Centro inicial cuando aún no hay pin (p. ej. el municipio elegido).
  centroInicial = null,
}) {
  const [buscar, setBuscar]     = useState('');
  const [buscando, setBuscando] = useState(false);
  const [errorBusq, setError]   = useState('');
  // Google llama window.gm_authFailure cuando rechaza la clave (referer no
  // autorizado, API no habilitada, clave inválida o sin facturación). En ese
  // caso useLoadScript NO reporta loadError y el mapa queda en gris con
  // "Se ha producido un error"; se detecta aquí para ofrecer el respaldo.
  const [authFallo, setAuthFallo] = useState(false);
  useEffect(() => {
    const previo = window.gm_authFailure;
    window.gm_authFailure = () => {
      setAuthFallo(true);
      if (typeof previo === 'function') previo();
    };
    return () => { window.gm_authFailure = previo; };
  }, []);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: MAPS_KEY,
    language: 'es',
    region:   'CO',
  });

  const hasCoords = lat != null && lng != null && !isNaN(lat) && !isNaN(lng);
  const center    = hasCoords
    ? { lat: +lat, lng: +lng }
    : (centroInicial?.lat != null && centroInicial?.lng != null
        ? { lat: +centroInicial.lat, lng: +centroInicial.lng }
        : CENTRO_COLOMBIA);

  const onLoad = useCallback((map) => {
    mapRef.current = map;
    if (hasCoords) map.setZoom(ZOOM_CON_PIN);
  }, [hasCoords]);

  const onMapClick = (e) => onCoordsChange(e.latLng.lat(), e.latLng.lng());
  const onDragEnd  = (e) => onCoordsChange(e.latLng.lat(), e.latLng.lng());

  /** Búsqueda vía proxy backend — la clave de Google queda server-side. */
  const buscarDireccion = async (e) => {
    e?.preventDefault?.();
    if (!buscar.trim()) return;
    setBuscando(true); setError('');
    try {
      const r = await ubicacion.geocodificar(
        buscar.trim(), municipioNombre, departamentoNombre
      );
      if (!r.encontrado) {
        setError(r.mensaje || 'No se encontró la dirección.');
        return;
      }
      onCoordsChange(r.lat, r.lng);
      // Sube el zoom si hay mapa cargado
      if (mapRef.current) mapRef.current.setZoom(ZOOM_CON_PIN);
    } catch (err) {
      setError(err.mensajeUsuario || 'No se pudo buscar la dirección.');
    } finally {
      setBuscando(false);
    }
  };

  const usarMiUbicacion = () => {
    if (!navigator.geolocation) {
      notify?.('Tu navegador no soporta geolocalización.', 'warning');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => onCoordsChange(pos.coords.latitude, pos.coords.longitude),
      (err) => notify?.(`No se pudo obtener tu ubicación: ${err.message}`, 'error'),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  if (!MAPS_KEY || authFallo) {
    // Sin clave de Maps el pin igual se puede fijar con la ubicación del
    // navegador, para no bloquear el registro ni la edición de sucursales.
    return (
      <Stack spacing={1.5}>
        <Alert severity="warning">
          {MAPS_KEY
            ? 'El mapa no está disponible en este momento.'
            : <>El mapa no está disponible (falta <code>REACT_APP_GOOGLE_MAPS_KEY</code>).</>}
          {' '}Puedes fijar la ubicación con la de tu dispositivo estando en el negocio.
        </Alert>
        <Button variant="outlined" startIcon={<MyLocationIcon />} onClick={usarMiUbicacion}
                sx={{ alignSelf: 'flex-start' }}>
          Usar mi ubicación actual
        </Button>
        {hasCoords && (
          <Alert severity="success">
            Ubicación fijada: {(+lat).toFixed(6)}, {(+lng).toFixed(6)}
          </Alert>
        )}
      </Stack>
    );
  }
  if (loadError) {
    return (
      <Alert severity="error">
        No se pudo cargar Google Maps. Revisa que la clave esté restringida
        para <code>{window.location.host}</code> y tenga habilitadas
        Maps JavaScript API y Geocoding API.
      </Alert>
    );
  }
  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!permitirBusqueda && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
            Haz clic en el mapa o arrastra el pin hasta tu negocio.
          </Box>
          <Button size="small" startIcon={<MyLocationIcon />} onClick={usarMiUbicacion}>
            Mi ubicación
          </Button>
        </Stack>
      )}
      {permitirBusqueda && (
      <form onSubmit={buscarDireccion}>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <TextField
            fullWidth size="small"
            placeholder="Buscar dirección (ej. Cra 27 #52-33, Bucaramanga)"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            disabled={buscando}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button type="submit" variant="outlined" disabled={buscando || !buscar.trim()}>
            {buscando ? 'Buscando…' : 'Buscar'}
          </Button>
          <IconButton onClick={usarMiUbicacion} title="Usar mi ubicación">
            <MyLocationIcon />
          </IconButton>
        </Stack>
      </form>
      )}
      {errorBusq && (
        <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setError('')}>
          {errorBusq}
        </Alert>
      )}

      <Box sx={{
        borderRadius: 2, overflow: 'hidden',
        border: '1px solid', borderColor: 'divider',
      }}>
        <GoogleMap
          mapContainerStyle={CONTAINER_STYLE}
          center={center}
          zoom={hasCoords ? ZOOM_CON_PIN : (centroInicial?.lat != null ? 13 : ZOOM_INICIAL)}
          options={MAP_OPTIONS}
          onLoad={onLoad}
          onClick={onMapClick}
        >
          {hasCoords && (
            <MarkerF
              position={{ lat: +lat, lng: +lng }}
              draggable
              onDragEnd={onDragEnd}
            />
          )}
        </GoogleMap>
      </Box>

      {hasCoords ? (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
          <TextField
            size="small" label="Latitud" type="number" value={lat}
            onChange={(e) => onCoordsChange(parseFloat(e.target.value), lng)}
            sx={{ width: 160 }} inputProps={{ step: 'any' }}
          />
          <TextField
            size="small" label="Longitud" type="number" value={lng}
            onChange={(e) => onCoordsChange(lat, parseFloat(e.target.value))}
            sx={{ width: 160 }} inputProps={{ step: 'any' }}
          />
        </Stack>
      ) : (
        <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
          {permitirBusqueda
            ? 'Sin coordenadas todavía. Haz clic en el mapa donde está la sucursal, busca una dirección, o usa el botón de ubicación.'
            : 'Sin coordenadas todavía. Haz clic en el mapa donde está tu negocio o usa "Mi ubicación".'}
        </Alert>
      )}
    </Box>
  );
}
